import React, { useMemo, useState } from "react";
import BaseModal from "../modals/BaseModal";

const initialForm = {
  name: "",
  mobile: "",
  email: "",
  instagramUsername: "",
  followers: "",
  following: "",
  location: "",
};

function toPositiveNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {boolean} props.submitting
 * @param {(payload: any) => Promise<void>} props.onSubmit
 * @param {string} [props.campaignName]
 */
function InviteInfluencerModal({
  open,
  onClose,
  submitting,
  onSubmit,
  campaignName = "",
}) {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");

  const normalizedUsername = useMemo(
    () => String(form.instagramUsername || "").trim().replace(/^@/, ""),
    [form.instagramUsername]
  );

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleClose = () => {
    if (submitting) return;
    setError("");
    setForm(initialForm);
    onClose();
  };

  const handleSubmit = async () => {
    setError("");
    if (!normalizedUsername) {
      setError("Instagram username is required.");
      return;
    }

    const followers = form.followers === "" ? undefined : toPositiveNumber(form.followers);
    const following = form.following === "" ? undefined : toPositiveNumber(form.following);

    if (form.followers !== "" && followers == null) {
      setError("Followers must be a non-negative number.");
      return;
    }
    if (form.following !== "" && following == null) {
      setError("Following must be a non-negative number.");
      return;
    }

    try {
      await onSubmit({
        ...form,
        instagramUsername: normalizedUsername,
        followers,
        following,
      });
      setForm(initialForm);
      setError("");
    } catch (e) {
      setError(e?.message || "Failed to invite influencer");
    }
  };

  return (
    <BaseModal
      open={open}
      title="Invite influencer"
      onClose={handleClose}
      className="max-w-[700px]"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-[#E3DFD9] text-sm font-semibold text-[#6B6560] hover:bg-[#F7F5F2] disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-[#E65C1A] text-white text-sm font-semibold hover:bg-[#cc4e13] disabled:opacity-60"
          >
            {submitting ? "Inviting..." : "Invite"}
          </button>
        </div>
      }
    >
      <div className="p-5 space-y-4">
        {campaignName ? (
          <p className="text-xs text-[#6B6560]">
            Campaign: <span className="font-semibold">{campaignName}</span>
          </p>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-[#6B6560]">Name</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              className="rounded-lg border border-[#E3DFD9] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-[#6B6560]">Mobile No.</span>
            <input
              type="text"
              value={form.mobile}
              onChange={(e) => updateField("mobile", e.target.value)}
              className="rounded-lg border border-[#E3DFD9] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-[#6B6560]">Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
              className="rounded-lg border border-[#E3DFD9] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-[#6B6560]">
              Instagram username <span className="text-[#E65C1A]">*</span>
            </span>
            <input
              type="text"
              value={form.instagramUsername}
              onChange={(e) => updateField("instagramUsername", e.target.value)}
              placeholder="@username"
              className="rounded-lg border border-[#E3DFD9] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-[#6B6560]">Followers</span>
            <input
              type="number"
              min="0"
              value={form.followers}
              onChange={(e) => updateField("followers", e.target.value)}
              className="rounded-lg border border-[#E3DFD9] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-[#6B6560]">Following</span>
            <input
              type="number"
              min="0"
              value={form.following}
              onChange={(e) => updateField("following", e.target.value)}
              className="rounded-lg border border-[#E3DFD9] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </label>

          <label className="flex flex-col gap-1 sm:col-span-2">
            <span className="text-xs font-semibold text-[#6B6560]">Location</span>
            <input
              type="text"
              value={form.location}
              onChange={(e) => updateField("location", e.target.value)}
              className="rounded-lg border border-[#E3DFD9] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </label>
        </div>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}
      </div>
    </BaseModal>
  );
}

export default InviteInfluencerModal;
